import React, { useState, useEffect } from 'react';
import { View, TouchableOpacity } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useTranslation } from 'react-i18next';
import { Link } from 'expo-router';
import { useKavVerticalOffset } from '@/hooks/useKavVerticalOffset';
import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';

import { Text } from '@/components/gluestack/text';
import { Input, InputField, InputSlot, InputIcon } from '@/components/gluestack/input';
import { Card } from '@/components/gluestack/card';
import { HStack } from '@/components/gluestack/hstack';
import { CloseIcon } from '@/components/gluestack/icon';
import { Search02Icon } from '@hugeicons-pro/core-solid-rounded';
import { UserSearch01Icon } from '@hugeicons-pro/core-stroke-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { FlashList } from '@shopify/flash-list';
import { PostsService } from '~/services/domain/PostsService';
import { useQuery } from '@tanstack/react-query';
import { Heading } from '@/components/gluestack/heading';
import type { SearchedTerms } from '@/models/SearchedTerms';

interface PostSearchProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  autoFocus?: boolean;
}

export const PostSearch = ({
  searchQuery,
  onSearchQueryChange: setSearchQuery,
  autoFocus = false,
}: PostSearchProps) => {
  const { t } = useTranslation();
  const isLargeScreen = useIsLargeScreen();
  const { kavRef, kavVerticalOffset, handleKavLayout } = useKavVerticalOffset();
  const [inputValue, setInputValue] = useState(searchQuery);

  const { data: searchTerms } = useQuery<SearchedTerms>({
    queryKey: ['searchedTerms'],
    queryFn: () => PostsService.getSearchedTerms(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Keep inputValue in sync with searchQuery from props
  useEffect(() => {
    setInputValue(searchQuery);
  }, [searchQuery]);

  const handleSearch = () => {
    setSearchQuery(inputValue);
  };

  const clearSearch = () => {
    setInputValue('');
    setSearchQuery('');
  };

  const handleTermPress = (term: string) => {
    setInputValue(term);
    setSearchQuery(term);
  };

  // Create a data structure for the FlashList sections
  const getSectionData = () => {
    if (!searchTerms) return [];

    const sections: Array<{ title: string; data: string[]; type: string }> = [];

    if ((searchTerms.latestSearches?.length ?? 0) > 0) {
      sections.push({
        title: t('feeds.latestSearches'),
        data: searchTerms.latestSearches ?? [],
        type: 'latest',
      });
    }

    if (searchTerms?.popularSearches?.length > 0) {
      sections.push({
        title: t('feeds.popularSearches'),
        data: searchTerms.popularSearches,
        type: 'search',
      });
    }

    if (searchTerms?.popularHashtags?.length > 0) {
      sections.push({
        title: t('feeds.popularHashtags'),
        data: searchTerms.popularHashtags,
        type: 'hashtag',
      });
    }

    return sections;
  };

  // Prepare sections data outside of render for better performance
  const sections = getSectionData();

  return (
    <KeyboardAvoidingView
      ref={kavRef}
      behavior="padding"
      keyboardVerticalOffset={kavVerticalOffset}
      style={searchQuery.length === 0 ? { flex: 1 } : undefined}
      onLayout={handleKavLayout}>
      <Input className="m-4" variant="rounded">
        <InputSlot className="pl-3">
          <HugeIconsWrapper icon={Search02Icon} size={16} className="color-typography-700" />
        </InputSlot>
        <InputField
          placeholder={t('feeds.search_placeholder')}
          value={inputValue}
          onChangeText={(text) => setInputValue(text)}
          returnKeyType="search"
          onSubmitEditing={handleSearch}
          autoFocus={autoFocus}
        />
        {inputValue.length > 0 && (
          <InputSlot className="pr-2" onPress={clearSearch}>
            <InputIcon as={CloseIcon} />
          </InputSlot>
        )}
      </Input>
      {searchQuery.length === 0 && (
        <FlashList
          data={sections}
          contentContainerStyle={{ paddingBottom: 16 }}
          ListHeaderComponent={
            <Link href="/people/search" asChild>
              <TouchableOpacity activeOpacity={0.7}>
                <Card
                  variant={isLargeScreen ? 'ghost' : 'outline'}
                  size="sm"
                  className="mx-4 mb-4">
                  <HStack space="md" className="items-center">
                    <HugeIconsWrapper icon={UserSearch01Icon} size={20} className="color-primary-500" />
                    <Text className="color-typography-700">
                      {t('feeds.searchMembersHint')}{' '}
                      <Text className="font-semibold color-primary-500 web:hover:underline">
                        {t('feeds.searchMembersLink')}
                      </Text>
                    </Text>
                  </HStack>
                </Card>
              </TouchableOpacity>
            </Link>
          }
          renderItem={({ item: section }) => (
            <View className="m-4 mb-0">
              <Heading size="sm" className="mb-2 px-1 color-typography-900">
                {section.title}
              </Heading>
              <View className="flex-row flex-wrap">
                {section.data.map((term, index) => (
                  <TouchableOpacity
                    key={`${section.type}-${index}`}
                    onPress={() => handleTermPress(term)}
                    className="mb-2 mr-2 rounded-full bg-background-200 px-3 py-1.5">
                    <Text className="color-typography-700">{term}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}
          keyExtractor={(item, index) => `section-${item.type}-${index}`}
        />
      )}
    </KeyboardAvoidingView>
  );
};
